import apiClient from './client';
import { ApiResponse, Pagination } from '@/types';

/**
 * Job status
 */
export type JobStatus = 'success' | 'failure' | 'running' | 'skipped';

/**
 * Job trigger type
 */
export type JobTrigger = 'scheduler' | 'manual' | 'webhook' | 'system';

/**
 * Job info
 */
export interface Job {
  name: string;
  schedule: string;
  enabled: boolean;
  lastRun?: string;
  lastResult?: 'success' | 'failure' | 'skipped';
  lastError?: string;
  lastDurationMs?: number;
  stats?: {
    totalRuns: number;
    successCount: number;
    failureCount: number;
    avgDurationMs: number;
    successRate: number;
  };
}

/**
 * Job run history record
 */
export interface JobRun {
  _id: string;
  jobName: string;
  status: JobStatus;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  error?: string;
  errorStack?: string;
  metadata?: Record<string, unknown>;
  triggeredBy: JobTrigger;
  triggeredByUserId?: string;
  createdAt: string;
}

/**
 * Job summary with stats
 */
export interface JobSummary {
  jobName: string;
  totalRuns: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  avgDurationMs: number;
  lastRun?: string;
  lastStatus?: JobStatus;
}

/**
 * Get all jobs with status
 */
export async function getJobs(): Promise<Job[]> {
  const response = await apiClient.get<ApiResponse<{ isRunning: boolean; jobs: Job[] }>>('/jobs');
  return (response.data.data as { isRunning: boolean; jobs: Job[] }).jobs;
}

/**
 * Get job run history
 */
export async function getJobHistory(options?: {
  jobName?: string;
  status?: JobStatus;
  limit?: number;
  offset?: number;
}): Promise<{ data: JobRun[]; pagination: Pagination }> {
  const params = new URLSearchParams();
  if (options?.jobName) params.append('jobName', options.jobName);
  if (options?.status) params.append('status', options.status);
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  const url = `/jobs/history${params.toString() ? `?${params}` : ''}`;
  const response = await apiClient.get<ApiResponse<JobRun[]>>(url);

  const meta = response.data.meta as { pagination?: { total: number; limit: number; offset: number } };
  const paginationData = meta?.pagination || { total: 0, limit: options?.limit || 50, offset: options?.offset || 0 };

  return {
    data: response.data.data as JobRun[],
    pagination: {
      page: Math.floor(paginationData.offset / paginationData.limit) + 1,
      limit: paginationData.limit,
      total: paginationData.total,
      totalPages: Math.ceil(paginationData.total / paginationData.limit),
      hasNext: paginationData.offset + paginationData.limit < paginationData.total,
      hasPrev: paginationData.offset > 0,
    },
  };
}

/**
 * Get job summaries with statistics
 */
export async function getJobSummaries(): Promise<JobSummary[]> {
  const response = await apiClient.get<ApiResponse<JobSummary[]>>('/jobs/summaries');
  return response.data.data as JobSummary[];
}

/**
 * Run a job manually
 */
export async function runJob(jobName: string): Promise<{ message: string; jobName: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string; jobName: string }>>(
    `/jobs/${jobName}/run`
  );
  return response.data.data as { message: string; jobName: string };
}

/**
 * Enable a job
 */
export async function enableJob(jobName: string): Promise<{ message: string; jobName: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string; jobName: string }>>(
    `/jobs/${jobName}/enable`
  );
  return response.data.data as { message: string; jobName: string };
}

/**
 * Disable a job
 */
export async function disableJob(jobName: string): Promise<{ message: string; jobName: string }> {
  const response = await apiClient.post<ApiResponse<{ message: string; jobName: string }>>(
    `/jobs/${jobName}/disable`
  );
  return response.data.data as { message: string; jobName: string };
}
